import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ShieldCheck, Check, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { PLAN_LABELS, PLAN_PRICES, type PlanType } from "@/lib/subscriptionContext";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";

const PLAN_FEATURES: Record<string, string[]> = {
  pro: ["10 roadmaps", "Everything in Free, plus more", "Location map", "Unlimited AI assistant", "Post-opening operations"],
  business: ["25 roadmaps", "Everything in Pro", "5 team members", "White-label PDF", "Analytics dashboard"],
  enterprise: ["Unlimited everything", "SSO", "API access", "Custom integrations", "Dedicated onboarding"],
};

export default function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planKey = (searchParams.get("plan") || "pro") as PlanType;
  const initialBilling = searchParams.get("billing") === "yearly" ? "yearly" : "monthly";

  const { user } = useAuth();

  const [billing, setBilling] = useState<"monthly" | "yearly">(initialBilling);
  const planLabel = PLAN_LABELS[planKey] || "Pro";
  const price = billing === "yearly" ? PLAN_PRICES[planKey]?.yearly : PLAN_PRICES[planKey]?.monthly;
  const monthlyPrice = PLAN_PRICES[planKey]?.monthly || 0;
  const displayPrice = billing === "yearly" ? Math.round((price || 0) / 12) : price || 0;
  const features = PLAN_FEATURES[planKey] || PLAN_FEATURES.pro;
  const yearlySavings = monthlyPrice * 12 - (PLAN_PRICES[planKey]?.yearly || 0);

  const [processing, setProcessing] = useState(false);

  const handleContinue = async () => {
    setProcessing(true);
    try {
      if (!user) {
        navigate("/auth");
        return;
      }
      // Map displayed plan key to backend plan_type ("studio" → "business").
      // Starter tier removed — only "pro" and "business" are purchasable here.
      const backendPlan = planKey === ("studio" as PlanType) ? "business" : planKey;
      if (!["pro", "business"].includes(backendPlan)) {
        toast.error("This plan can't be purchased here. Please contact support.");
        setProcessing(false);
        return;
      }
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { plan: backendPlan, billing, origin: window.location.origin },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url as string;
        return;
      }
      throw new Error("No checkout URL returned");
    } catch (err) {
      console.error(err);
      toast.error("Could not start checkout. Please try again.");
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* ─── Header ─── */}
      <div className="border-b border-border bg-card sticky top-0 z-20">
        <div className="container flex items-center justify-center py-4">
          <span
            className="font-heading text-xl font-medium tracking-tight text-foreground cursor-pointer"
            onClick={() => navigate("/")}
          >
            oplif<span className="text-primary">.</span>
          </span>
        </div>
      </div>

      <div className="container max-w-2xl py-8 lg:py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4 text-primary" />
            <span className="text-xs font-medium text-muted-foreground">Secure checkout — powered by Stripe</span>
          </div>
          <h1 className="font-heading text-2xl font-medium text-foreground mb-1">Confirm your plan</h1>
          <p className="text-sm text-muted-foreground mb-8">
            Review your order. You'll enter card details on Stripe's secure page — we never see or store your card.
          </p>
        </motion.div>

          {/* ─── RIGHT: Order Summary ─── */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="bg-card rounded-2xl border border-border p-6 shadow-card sticky top-24">
              <h2 className="font-heading font-medium text-foreground text-lg mb-5">Order summary</h2>

              {/* Billing toggle */}
              <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/60 border border-border/40 mb-5">
                <button
                  type="button"
                  onClick={() => setBilling("monthly")}
                  className={`flex-1 text-sm font-medium py-2 rounded-md transition-all ${
                    billing === "monthly"
                      ? "bg-card text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBilling("yearly")}
                  className={`flex-1 text-sm font-medium py-2 rounded-md transition-all relative ${
                    billing === "yearly"
                      ? "bg-card text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Annual
                  {yearlySavings > 0 && (
                    <span className="absolute -top-2 -right-1 text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">
                      Save ${yearlySavings}
                    </span>
                  )}
                </button>
              </div>

              {/* Plan info */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-heading font-medium text-foreground">{planLabel} Plan</div>
                  <span className="text-xs text-muted-foreground capitalize">{billing} billing</span>
                </div>
                <div className="text-right">
                  <span className="font-heading text-xl font-medium text-foreground">
                    ${displayPrice}
                  </span>
                  <span className="text-xs text-muted-foreground">/mo</span>
                </div>
              </div>

              <Separator className="mb-4" />

              {/* Features */}
              <ul className="space-y-2 mb-5">
                {features.map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Separator className="mb-4" />

              {/* Price breakdown */}
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-foreground font-medium">
                    ${billing === "yearly" ? (price || 0).toLocaleString() : displayPrice}
                    <span className="text-muted-foreground font-normal">/{billing === "yearly" ? "yr" : "mo"}</span>
                  </span>
                </div>
                {billing === "yearly" && yearlySavings > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Annual discount</span>
                    <span className="text-primary font-medium">-${yearlySavings}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span className="text-muted-foreground text-xs">Calculated at payment</span>
                </div>
              </div>

              <Separator className="mb-4" />

              {/* Total */}
              <div className="flex justify-between items-baseline">
                <span className="font-heading font-medium text-foreground">Total due today</span>
                <div className="text-right">
                  <span className="font-heading text-2xl font-medium text-foreground">
                    ${billing === "yearly" ? (price || 0).toLocaleString() : displayPrice}
                  </span>
                  <div className="text-[11px] text-muted-foreground">
                    /{billing === "yearly" ? "year" : "month"}
                  </div>
                </div>
              </div>

              {billing === "yearly" && (
                <p className="text-[11px] text-muted-foreground mt-2 text-center">
                  That's just ${displayPrice}/mo — billed annually
                </p>
              )}

              <Button
                onClick={handleContinue}
                disabled={processing}
                className="w-full h-12 mt-6 gap-2"
              >
                {processing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />Redirecting to Stripe…</>
                ) : (
                  <>Continue to secure payment <ArrowRight className="w-4 h-4" /></>
                )}
              </Button>

              <div className="flex items-center justify-center gap-2 mt-3 text-muted-foreground">
                <ShieldCheck className="w-3.5 h-3.5 text-primary" />
                <span className="text-[11px] font-medium">Card details handled by Stripe — PCI compliant</span>
              </div>

              {/* Cancellation policy */}
              <p className="mt-5 text-[11px] text-muted-foreground text-center leading-relaxed">
                Cancel anytime before your next billing date. No refunds on active periods.
              </p>
            </div>
          </motion.div>
      </div>
    </div>
  );
}
